import { isAdmin, isSuperAdmin } from '../../utils/authUtils';

/**
 * The internal admin tooling nav, in one place for both surfaces that show it: RedesignSidebar on
 * desktop and the More sheet on a phone (via RedesignLayout). It lived inside the sidebar until
 * the phone needed it too — and a phone had no route to any admin screen at all.
 */

export interface AdminLink {
    label: string;
    to: string;
    glyph: string;
}

/**
 * The internal tooling, copied path-for-path from `AppSidebar`'s `managementItems` so the two
 * sidebars cannot disagree about who sees what. Visible to Admin and SuperAdmin alike.
 *
 * Deliberately NOT in `navigation.ts`: that file is the client-facing nav shared with
 * `MobileTabBar`, and admin tooling is not a client tab. Shared instead by the desktop sidebar and
 * the phone's More sheet, so the two can never offer an admin different tools.
 */
export const MANAGEMENT_LINKS: AdminLink[] = [
    { label: 'Subscriptions', to: '/admin/subscriptions', glyph: '▤' },
    { label: 'Contact Requests', to: '/admin/contact-requests', glyph: '✎' },
    { label: 'Exclusive Dispatches', to: '/admin/exclusive-dispatches', glyph: '✈' },
    { label: 'Send my Business', to: '/admin/info-packs', glyph: '✉' },
    { label: 'Sales Pipeline', to: '/admin/deals', glyph: '⬈' },
    { label: 'Security', to: '/admin/security', glyph: '⚿' },
];

/**
 * `AppSidebar`'s `adminItems` — SuperAdmin only. Note that four of these are not under `/admin/`;
 * they are top-level routes in `App.tsx` that happen to be internal tooling.
 *
 * This is UI gating only. The server enforces the real permission on every one of these screens
 * (and `/admin/background-services` + `/admin/email-preview` additionally sit behind `AdminRoute`);
 * mirroring it here just keeps the redesign sidebar honest about what it offers.
 */
export const SUPER_ADMIN_LINKS: AdminLink[] = [
    { label: 'Background Services', to: '/admin/background-services', glyph: '⚙' },
    { label: 'Email Preview', to: '/admin/email-preview', glyph: '✉' },
    { label: 'Data Extraction', to: '/data-extraction', glyph: '⌕' },
    { label: 'File Uploader', to: '/file-uploader', glyph: '↥' },
    { label: 'File Downloader', to: '/file-downloader', glyph: '↧' },
    { label: 'PDF Uploader', to: '/pdf-uploader', glyph: '▣' },
    { label: 'Business Details', to: '/google-places', glyph: '⌂' },
];

/**
 * Whether the current route lives inside the admin section, so it can start open there rather
 * than leaving the sidebar looking like it has no idea where you are.
 *
 * Prefix-matched, not equality: `/admin/subscriptions/:id` is a real route and is still "inside"
 * the Subscriptions screen.
 */
export const isAdminPath = (pathname: string): boolean =>
    [...MANAGEMENT_LINKS, ...SUPER_ADMIN_LINKS].some(
        ({ to }) => pathname === to || pathname.startsWith(`${to}/`),
    );

/**
 * The groups a user may see, in order: Admin and SuperAdmin both get Management; only SuperAdmin
 * gets Admin. A group with no links is dropped entirely, so an Admin never sees an empty heading,
 * and a client gets an empty array.
 */
export function adminGroupsFor(user: Parameters<typeof isAdmin>[0]): { key: string; heading: string; links: AdminLink[] }[] {
    if (!isAdmin(user)) return [];
    return [
        { key: 'management', heading: 'Management', links: MANAGEMENT_LINKS },
        { key: 'admin', heading: 'Admin', links: isSuperAdmin(user) ? SUPER_ADMIN_LINKS : [] },
    ].filter((group) => group.links.length > 0);
}
